export enum PERMISSION {
  VIEW_EXPERIENCE = 'experience:read',
  MANAGE_EXPERIENCE = 'experience:manage',
  EDIT_EXPERIENCE = 'experience:edit',
  CURATE_EXPERIENCE = 'experience:curate',
  CREATE_BOOKING = 'booking:create',
  VIEW_BOOKING = 'booking:read',
  VOID_BOOKING = 'booking:void',
  REFUND_BOOKING = 'booking:refund',
  CANCEL_BOOKING = 'booking:cancel',
  RESCHEDULE_BOOKING = 'booking:reschedule',
  CHECK_IN_BOOKING = 'booking:check-in',
  VIEW_ALL_BOOKING = 'booking:viewAll',
  OVERRIDE_BOOKING_CAPACITY = 'booking:overrideCapacity',
  OVERRIDE_BOOKING_CUTOFF = 'booking:overrideCutoff',
  VIEW_SESSION = 'session:read',
  EDIT_SESSION = 'session:edit',
  VIEW_HOST = 'host:read',
  VIEW_HOST_FINANCIAL = 'host:readFinancial',
  VIEW_HOST_INFO = 'host:readInfo',
  MANAGE_HOST_IAM = 'host:manageIAM',
  VIEW_ANALYTICS = 'analytics:read',
  VIEW_BRAND_FINANCIAL = 'brand:readFinancial',
  VIEW_FINANCIAL_REPORT = 'financialReport:read',
  VIEW_GUEST_REPORT = 'guestReport:read',
  VIEW_EXPERIENCE_REPORT = 'experienceReport:read',
  MANAGE_BUSINESS_INFO = 'businessInfo:manage',
  MANAGE_BANK_ACCOUNT = 'bankAccount:manage',
  MANAGE_BRAND_IAM = 'brand:manageIAM',
  MANAGE_BRANDING = 'branding:manage',
  MANAGE_CATEGORY_VIBE = 'category:manage',
  MANAGE_EMAIL = 'email:manage',
  MANAGE_ROOM_NUMBER = 'roomNumber:manage',
  MANAGE_LEGAL_DOCUMENT = 'legalDocument:manage',
  MANAGE_CODE = 'code:manage',
  MANAGE_TAX_FEE = 'taxFee:manage',
  MANAGE_DEVELOPER = 'developer:manage',
  MANAGE_INTEGRATION = 'integration:manage',
  VIEW_REVIEW = 'review:read',
  VIEW_MESSAGE = 'messages:read',
  SEND_MESSAGE = 'messages:send',
  VIEW_BUSINESS_INFO = 'businessInfo:read',
  VIEW_CATEGORY_VIBE = 'category:read',
  VIEW_TAX_FEE = 'tax:read',
  MANAGE_MINIMAL_EXPERIENCE = 'experience:manageMinimal',
  MANAGE_BRAND = 'brand:manage',
  VIEW_QUESTIONS_RESPONSE = 'booking:readCustomQuestionsResponse',
  MANAGE_QUESTIONS_RESPONSE = 'booking:manageCustomQuestionsResponse',
  VIEW_BRAND = 'brand:view',
  VIEW_STAFF_USER = 'staffUser:view',
  MANAGE_STAFF_USER = 'staffUser:manage',
  VIEW_USER = 'user:view',
  MANAGE_USER = 'user:manage',
  MANAGE_ORGANIZATION = 'organization:manage',
  MANAGE_GUEST_SURVEY = 'guestSurvey:manage',
  RESTRICTED_VIEW_LIMITED_BRAND = 'restricted:viewLimitedBrand',
  MANAGE_MEMBER_BENEFITS = 'memberBenefits:manage',
}
export const isPermission = (permission: unknown): permission is PERMISSION =>
  Object.values(PERMISSION).includes(permission as PERMISSION);

export const BRAND_MEMBERSHIP_PERMISSIONS = [
  PERMISSION.VIEW_EXPERIENCE,
  PERMISSION.MANAGE_EXPERIENCE,
  PERMISSION.EDIT_EXPERIENCE,
  PERMISSION.CREATE_BOOKING,
  PERMISSION.VIEW_BOOKING,
  PERMISSION.VOID_BOOKING,
  PERMISSION.REFUND_BOOKING,
  PERMISSION.CANCEL_BOOKING,
  PERMISSION.RESCHEDULE_BOOKING,
  PERMISSION.CHECK_IN_BOOKING,
  PERMISSION.OVERRIDE_BOOKING_CAPACITY,
  PERMISSION.OVERRIDE_BOOKING_CUTOFF,
  PERMISSION.VIEW_SESSION,
  PERMISSION.EDIT_SESSION,
  PERMISSION.VIEW_HOST,
  PERMISSION.VIEW_HOST_FINANCIAL,
  PERMISSION.VIEW_HOST_INFO,
  PERMISSION.MANAGE_HOST_IAM,
  PERMISSION.VIEW_ANALYTICS,
  PERMISSION.VIEW_BRAND_FINANCIAL,
  PERMISSION.VIEW_FINANCIAL_REPORT,
  PERMISSION.VIEW_EXPERIENCE_REPORT,
  PERMISSION.VIEW_GUEST_REPORT,
  PERMISSION.MANAGE_BUSINESS_INFO,
  PERMISSION.MANAGE_BANK_ACCOUNT,
  PERMISSION.MANAGE_BRAND_IAM,
  PERMISSION.MANAGE_BRANDING,
  PERMISSION.MANAGE_CATEGORY_VIBE,
  PERMISSION.MANAGE_EMAIL,
  PERMISSION.MANAGE_ROOM_NUMBER,
  PERMISSION.MANAGE_LEGAL_DOCUMENT,
  PERMISSION.MANAGE_CODE,
  PERMISSION.MANAGE_TAX_FEE,
  PERMISSION.MANAGE_DEVELOPER,
  PERMISSION.MANAGE_INTEGRATION,
  PERMISSION.VIEW_REVIEW,
  PERMISSION.VIEW_MESSAGE,
  PERMISSION.SEND_MESSAGE,
  PERMISSION.VIEW_BUSINESS_INFO,
  PERMISSION.VIEW_CATEGORY_VIBE,
  PERMISSION.VIEW_TAX_FEE,
  PERMISSION.MANAGE_MINIMAL_EXPERIENCE,
  PERMISSION.VIEW_QUESTIONS_RESPONSE,
  PERMISSION.MANAGE_QUESTIONS_RESPONSE,
  PERMISSION.RESTRICTED_VIEW_LIMITED_BRAND,
  PERMISSION.MANAGE_MEMBER_BENEFITS,
] as const satisfies ReadonlyArray<PERMISSION>;

export type BRAND_MEMBERSHIP_PERMISSION = (typeof BRAND_MEMBERSHIP_PERMISSIONS)[number];

export const isBrandMembershipPermission = (permission: unknown): permission is BRAND_MEMBERSHIP_PERMISSION =>
  BRAND_MEMBERSHIP_PERMISSIONS.includes(permission as BRAND_MEMBERSHIP_PERMISSION);

export const ORGANIZATION_MEMBERSHIP_PERMISSIONS = [
  ...BRAND_MEMBERSHIP_PERMISSIONS,
  PERMISSION.MANAGE_ORGANIZATION,
  PERMISSION.MANAGE_MEMBER_BENEFITS,
];

export type ORGANIZATION_MEMBERSHIP_PERMISSION = (typeof ORGANIZATION_MEMBERSHIP_PERMISSIONS)[number];
export const isOrganizationMembershipPermission = (permission: unknown): permission is ORGANIZATION_MEMBERSHIP_PERMISSION =>
  ORGANIZATION_MEMBERSHIP_PERMISSIONS.includes(permission as ORGANIZATION_MEMBERSHIP_PERMISSION);

export const ADMIN_PERMISSIONS = [
  PERMISSION.VIEW_EXPERIENCE,
  PERMISSION.MANAGE_EXPERIENCE,
  PERMISSION.EDIT_EXPERIENCE,
  PERMISSION.CREATE_BOOKING,
  PERMISSION.VIEW_BOOKING,
  PERMISSION.VOID_BOOKING,
  PERMISSION.REFUND_BOOKING,
  PERMISSION.CANCEL_BOOKING,
  PERMISSION.RESCHEDULE_BOOKING,
  PERMISSION.CHECK_IN_BOOKING,
  PERMISSION.OVERRIDE_BOOKING_CAPACITY,
  PERMISSION.OVERRIDE_BOOKING_CUTOFF,
  PERMISSION.VIEW_SESSION,
  PERMISSION.EDIT_SESSION,
  PERMISSION.VIEW_HOST,
  PERMISSION.VIEW_HOST_FINANCIAL,
  PERMISSION.VIEW_HOST_INFO,
  PERMISSION.MANAGE_HOST_IAM,
  PERMISSION.VIEW_ANALYTICS,
  PERMISSION.VIEW_BRAND_FINANCIAL,
  PERMISSION.VIEW_FINANCIAL_REPORT,
  PERMISSION.VIEW_EXPERIENCE_REPORT,
  PERMISSION.VIEW_GUEST_REPORT,
  PERMISSION.MANAGE_BUSINESS_INFO,
  PERMISSION.MANAGE_BANK_ACCOUNT,
  PERMISSION.MANAGE_BRAND_IAM,
  PERMISSION.MANAGE_BRANDING,
  PERMISSION.MANAGE_CATEGORY_VIBE,
  PERMISSION.MANAGE_EMAIL,
  PERMISSION.MANAGE_ROOM_NUMBER,
  PERMISSION.MANAGE_LEGAL_DOCUMENT,
  PERMISSION.MANAGE_CODE,
  PERMISSION.MANAGE_TAX_FEE,
  PERMISSION.MANAGE_DEVELOPER,
  PERMISSION.MANAGE_INTEGRATION,
  PERMISSION.VIEW_REVIEW,
  PERMISSION.MANAGE_MINIMAL_EXPERIENCE,
  PERMISSION.VIEW_QUESTIONS_RESPONSE,
  PERMISSION.MANAGE_QUESTIONS_RESPONSE,
  PERMISSION.VIEW_MESSAGE,
  PERMISSION.SEND_MESSAGE,
  PERMISSION.VIEW_CATEGORY_VIBE,
  PERMISSION.RESTRICTED_VIEW_LIMITED_BRAND,
  PERMISSION.MANAGE_MEMBER_BENEFITS,
] as const satisfies ReadonlyArray<BRAND_MEMBERSHIP_PERMISSION>;

export type ADMIN_PERMISSION = (typeof ADMIN_PERMISSIONS)[number];

export const isAdminPermission = (permission: unknown): permission is ADMIN_PERMISSION =>
  ADMIN_PERMISSIONS.includes(permission as ADMIN_PERMISSION);

export const HOST_PERMISSIONS = [
  PERMISSION.VIEW_BOOKING,
  PERMISSION.VIEW_BUSINESS_INFO,
  PERMISSION.VIEW_CATEGORY_VIBE,
  PERMISSION.VIEW_TAX_FEE,
  PERMISSION.MANAGE_MINIMAL_EXPERIENCE,
  PERMISSION.VIEW_HOST_FINANCIAL,
  PERMISSION.VIEW_QUESTIONS_RESPONSE,
  PERMISSION.MANAGE_QUESTIONS_RESPONSE,
  PERMISSION.VIEW_MESSAGE,
  PERMISSION.SEND_MESSAGE,
  PERMISSION.CHECK_IN_BOOKING,
] as const satisfies ReadonlyArray<BRAND_MEMBERSHIP_PERMISSION>;

export type HOST_PERMISSION = (typeof HOST_PERMISSIONS)[number];

export const isHostPermission = (permission: unknown): permission is HOST_PERMISSION =>
  HOST_PERMISSIONS.includes(permission as HOST_PERMISSION);

export const INTEGRATION_PERMISSIONS = [
  PERMISSION.REFUND_BOOKING,
  PERMISSION.CANCEL_BOOKING,
  PERMISSION.VOID_BOOKING,
  PERMISSION.EDIT_SESSION,
] as const satisfies ReadonlyArray<BRAND_MEMBERSHIP_PERMISSION>;

export type INTEGRATION_PERMISSION = (typeof INTEGRATION_PERMISSIONS)[number];

export const isIntegrationPermission = (permission: unknown): permission is INTEGRATION_PERMISSION =>
  INTEGRATION_PERMISSIONS.includes(permission as INTEGRATION_PERMISSION);

export const ADDITIONAL_HOST_PERMISSIONS = [
  PERMISSION.CANCEL_BOOKING,
  PERMISSION.RESCHEDULE_BOOKING,
  PERMISSION.VOID_BOOKING,
  PERMISSION.REFUND_BOOKING,
  PERMISSION.CREATE_BOOKING,
] as const satisfies ReadonlyArray<BRAND_MEMBERSHIP_PERMISSION>;

export type ADDITIONAL_HOST_PERMISSION = (typeof ADDITIONAL_HOST_PERMISSIONS)[number];

export const isAdditionalHostPermission = (permission: unknown): permission is ADDITIONAL_HOST_PERMISSION =>
  ADDITIONAL_HOST_PERMISSIONS.includes(permission as ADDITIONAL_HOST_PERMISSION);

export const STAFF_ONLY_PERMISSIONS = [
  PERMISSION.VIEW_CATEGORY_VIBE,
  PERMISSION.VIEW_BUSINESS_INFO,
  PERMISSION.VIEW_TAX_FEE,
  PERMISSION.VIEW_STAFF_USER,
  PERMISSION.MANAGE_STAFF_USER,
  PERMISSION.CURATE_EXPERIENCE,
  PERMISSION.VIEW_BRAND,
  PERMISSION.MANAGE_BRAND,
  PERMISSION.MANAGE_USER,
  PERMISSION.VIEW_USER,
  PERMISSION.VIEW_ALL_BOOKING,
  PERMISSION.MANAGE_ORGANIZATION,
  PERMISSION.MANAGE_GUEST_SURVEY,
];

export const ORGANIZATION_ONLY_PERMISSIONS = [PERMISSION.MANAGE_ORGANIZATION, PERMISSION.MANAGE_MEMBER_BENEFITS];

export const ACCOUNT_EXECUTIVE_PERMISSIONS = [
  PERMISSION.VIEW_EXPERIENCE,
  PERMISSION.VIEW_BOOKING,
  PERMISSION.VIEW_SESSION,
  PERMISSION.VIEW_HOST,
  PERMISSION.VIEW_HOST_FINANCIAL,
  PERMISSION.VIEW_HOST_INFO,
  PERMISSION.VIEW_ANALYTICS,
  PERMISSION.VIEW_BRAND_FINANCIAL,
  PERMISSION.VIEW_FINANCIAL_REPORT,
  PERMISSION.VIEW_EXPERIENCE_REPORT,
  PERMISSION.VIEW_GUEST_REPORT,
  PERMISSION.VIEW_REVIEW,
  PERMISSION.VIEW_BUSINESS_INFO,
  PERMISSION.VIEW_CATEGORY_VIBE,
  PERMISSION.VIEW_TAX_FEE,
  PERMISSION.VIEW_QUESTIONS_RESPONSE,
  PERMISSION.VIEW_MESSAGE,
  PERMISSION.VIEW_BRAND,
] as const satisfies ReadonlyArray<PERMISSION>;

export const CUSTOMER_SUCCESS_MANAGEMENT_PERMISSIONS = [
  ...ACCOUNT_EXECUTIVE_PERMISSIONS,
  PERMISSION.CREATE_BOOKING,
  PERMISSION.VOID_BOOKING,
  PERMISSION.REFUND_BOOKING,
  PERMISSION.CANCEL_BOOKING,
  PERMISSION.RESCHEDULE_BOOKING,
  PERMISSION.OVERRIDE_BOOKING_CAPACITY,
  PERMISSION.OVERRIDE_BOOKING_CUTOFF,
  PERMISSION.VIEW_ALL_BOOKING,
  PERMISSION.EDIT_SESSION,
  PERMISSION.VIEW_USER,
  PERMISSION.MANAGE_USER,
  PERMISSION.MANAGE_HOST_IAM,
  PERMISSION.MANAGE_BUSINESS_INFO,
  PERMISSION.MANAGE_BANK_ACCOUNT,
  PERMISSION.MANAGE_BRAND_IAM,
  PERMISSION.MANAGE_CATEGORY_VIBE,
  PERMISSION.MANAGE_EMAIL,
  PERMISSION.MANAGE_ROOM_NUMBER,
  PERMISSION.MANAGE_LEGAL_DOCUMENT,
  PERMISSION.MANAGE_CODE,
  PERMISSION.MANAGE_TAX_FEE,
  PERMISSION.MANAGE_DEVELOPER,
  PERMISSION.MANAGE_INTEGRATION,
  PERMISSION.MANAGE_MINIMAL_EXPERIENCE,
  PERMISSION.CURATE_EXPERIENCE,
  PERMISSION.MANAGE_QUESTIONS_RESPONSE,
  PERMISSION.MANAGE_EXPERIENCE,
  PERMISSION.EDIT_EXPERIENCE,
  PERMISSION.SEND_MESSAGE,
  PERMISSION.CHECK_IN_BOOKING,
  PERMISSION.MANAGE_ORGANIZATION,
  PERMISSION.MANAGE_MEMBER_BENEFITS,
  PERMISSION.VIEW_STAFF_USER,
] as const satisfies ReadonlyArray<PERMISSION>;

export const STAFF_ADMIN_PERMISSIONS = [
  ...CUSTOMER_SUCCESS_MANAGEMENT_PERMISSIONS,
  PERMISSION.MANAGE_BRAND,
  PERMISSION.MANAGE_STAFF_USER,
  PERMISSION.MANAGE_GUEST_SURVEY,
  PERMISSION.MANAGE_MEMBER_BENEFITS,
] as const satisfies ReadonlyArray<PERMISSION>;

export const SYSTEM_PERMISSIONS = [
  PERMISSION.MANAGE_EXPERIENCE,
  PERMISSION.EDIT_EXPERIENCE,
  PERMISSION.VIEW_BOOKING,
  PERMISSION.CANCEL_BOOKING,
  PERMISSION.REFUND_BOOKING,
] as const satisfies ReadonlyArray<BRAND_MEMBERSHIP_PERMISSION>;

export type SYSTEM_PERMISSIONS = (typeof SYSTEM_PERMISSIONS)[number];

export const LIMITED_BRAND_PERMISSIONS = [
  PERMISSION.CANCEL_BOOKING,
  PERMISSION.CHECK_IN_BOOKING,
  PERMISSION.CREATE_BOOKING,
  PERMISSION.EDIT_EXPERIENCE,
  PERMISSION.EDIT_SESSION,
  PERMISSION.MANAGE_BANK_ACCOUNT,
  PERMISSION.MANAGE_BRAND_IAM,
  PERMISSION.MANAGE_BUSINESS_INFO,
  PERMISSION.MANAGE_CATEGORY_VIBE,
  PERMISSION.MANAGE_EMAIL,
  PERMISSION.MANAGE_EXPERIENCE,
  PERMISSION.MANAGE_LEGAL_DOCUMENT,
  PERMISSION.MANAGE_QUESTIONS_RESPONSE,
  PERMISSION.MANAGE_TAX_FEE,
  PERMISSION.REFUND_BOOKING,
  PERMISSION.RESCHEDULE_BOOKING,
  PERMISSION.RESTRICTED_VIEW_LIMITED_BRAND,
  PERMISSION.SEND_MESSAGE,
  PERMISSION.VIEW_ANALYTICS,
  PERMISSION.VIEW_BOOKING,
  PERMISSION.VIEW_BRAND_FINANCIAL,
  PERMISSION.VIEW_BUSINESS_INFO,
  PERMISSION.VIEW_CATEGORY_VIBE,
  PERMISSION.VIEW_EXPERIENCE,
  PERMISSION.VIEW_FINANCIAL_REPORT,
  PERMISSION.VIEW_MESSAGE,
  PERMISSION.VIEW_QUESTIONS_RESPONSE,
  PERMISSION.VIEW_REVIEW,
  PERMISSION.VIEW_SESSION,
  PERMISSION.VOID_BOOKING,
] as const satisfies ReadonlyArray<PERMISSION>;
